import type { IconName } from '@customerio/pluma-icons';
import type { SearchResult, TemplateType } from '@/services/auth/types';
import { ACTION_TYPE_ICONS, TEMPLATE_TYPE_ICONS, CAMPAIGN_TYPE_ICONS } from './constants';

export function getIconNameFromSearchResult(result: SearchResult): IconName {
  switch (result.kind) {
    case 'campaign':
      return CAMPAIGN_TYPE_ICONS?.[result.other.campaign_type] ?? 'campaigns';
    case 'template':
      return TEMPLATE_TYPE_ICONS?.[result.other.template_type] ?? 'layout';
    case 'segment':
      return result.other.segment_type === 'static' ? 'segment-manual' : 'segment-data-driven';
    case 'action':
      return ACTION_TYPE_ICONS?.[result.other.action_type] ?? 'help';
    case 'navigation':
      return 'arrow-right';
    default:
      return 'search';
  }
}

function groupTemplatesByType(results: SearchResult[]) {
  const templateTypes: TemplateType[] = [];

  const groupedByTemplateType = results.reduce((groups, item) => {
    if (item.kind !== 'template') {
      return groups;
    }

    const templateType = item.other.template_type;

    if (groups[templateType] == null) {
      templateTypes.push(templateType);
      groups[templateType] = [];
    }

    groups[item.other.template_type].push(item);

    return groups;
  }, {} as Record<TemplateType, SearchResult[]>);

  return { groupedByTemplateType, templateTypes };
}

export function groupSearchResults(results: SearchResult[]) {
  // We are grouping templates by their type, so we exclude the template kind from the group type.
  type GroupType = Exclude<SearchResult['kind'], 'template'> | TemplateType;
  // First iteration preserves API order
  const resultsByKind = results.reduce((groups, item) => {
    const kind = item.kind;
    groups[kind] ??= [];
    groups[kind].push(item);
    return groups;
  }, {} as Record<SearchResult['kind'], SearchResult[]>);

  const entries: [GroupType, SearchResult[]][] = [];

  for (const [kind, items] of Object.entries(resultsByKind) as [SearchResult['kind'], SearchResult[]][]) {
    if (kind === 'template') {
      // Second pass: split template group by template_type; order = first occurrence in template list.
      const { groupedByTemplateType, templateTypes } = groupTemplatesByType(items);

      for (const t of templateTypes) {
        entries.push([t, groupedByTemplateType[t]]);
      }
    } else {
      entries.push([kind as GroupType, items]);
    }
  }

  return entries;
}
